import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import ContactApi from "../Api/ContactApi";
import { formatDate } from "../helper/DateHelper";
import TablePagination from "./TablePagination";

const ContactUsListLayer = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState("");

    const fetchMessages = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: rowsPerPage,
                search: search || undefined
            };
            const res = await ContactApi.getContactList(params);
            if (res.status) {
                setMessages(res.response.data || []);
                setTotalCount(res.response.total || 0);
            }
        } catch (error) {
            console.error("Error fetching contact messages:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this message?")) {
            const res = await ContactApi.deleteContact(id);
            if (res.status) {
                fetchMessages();
            }
        }
    };

    useEffect(() => {
        fetchMessages();
    }, [page, rowsPerPage]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden shadow-sm border-0">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h6 className="text-lg fw-bold mb-0 text-primary-600 d-flex align-items-center gap-2">
                        <Icon icon="solar:letter-bold-duotone" className="text-2xl" />
                        Contact Us Messages
                    </h6>
                    <div className="d-flex align-items-center gap-3">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control radius-8 ps-40-px h-40-px"
                                placeholder="Search messages..."
                                value={search}
                                onChange={handleSearch}
                            />
                            <Icon icon="solar:magnifer-linear" className="position-absolute start-16-px top-50 translate-middle-y text-secondary-light" />
                        </div>
                        <button 
                            className="btn btn-primary h-40-px d-flex align-items-center gap-2"
                            onClick={() => { setPage(0); fetchMessages(); }}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr className="bg-neutral-50 text-uppercase">
                                <th scope="col" className="text-center py-16" style={{ width: '80px', color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Name</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Contact Info</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Message</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Date</th>
                                <th scope="col" className="text-center py-16" style={{ color: "black", fontWeight: '600' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-100">
                                        <div className="spinner-border text-primary-600" role="status"></div>
                                    </td>
                                </tr>
                            ) : messages.length > 0 ? (
                                messages.map((item, index) => (
                                    <tr key={item._id}>
                                        <td className="text-center text-sm fw-medium text-secondary-light">
                                            {page * rowsPerPage + index + 1}
                                        </td>
                                        <td>
                                            <span className="text-sm fw-bold text-secondary-light">{item.name}</span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="text-xs text-secondary-light d-flex align-items-center gap-1">
                                                    <Icon icon="solar:phone-bold" className="text-xs" />
                                                    {item.mobile}
                                                </span>
                                                <span className="text-xs text-secondary-light d-flex align-items-center gap-1">
                                                    <Icon icon="solar:letter-bold" className="text-xs" />
                                                    {item.email}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: '300px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                {item.message}
                                            </p>
                                        </td>
                                        <td>
                                            <span className="text-sm text-secondary-light fw-medium">{formatDate(item.createdAt)}</span>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                onClick={() => handleDelete(item._id)}
                                                className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle mx-auto transition-all border-0"
                                                title="Delete Message"
                                            >
                                                <Icon icon="solar:trash-bin-trash-bold" className="text-xl" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-100 italic text-secondary-400">
                                        No contact messages found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalCount > 0 && (
                    <div className="mt-24">
                        <TablePagination
                            currentPage={page}
                            totalPages={Math.ceil(totalCount / rowsPerPage)}
                            onPageChange={(p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value));
                                setPage(0);
                            }}
                            totalRecords={totalCount}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactUsListLayer;
